import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatBRL, STATUS_STYLES, DOC_TYPES } from "@/lib/constants";
import { ArrowLeft, Phone, Mail, MapPin, FileText, MessageCircle, Plus, Wallet, Pencil, Wrench, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClientForm, EMPTY_CLIENT } from "@/components/ClientForm";
import { EquipmentDialog, MAINT_STYLE, MAINT_LABEL } from "@/components/EquipmentDialog";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_CLIENT);
  const [saving, setSaving] = useState(false);
  const [equipList, setEquipList] = useState([]);
  const [eqOpen, setEqOpen] = useState(false);
  const [eqInitial, setEqInitial] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const { user } = useAuth();
  const removeClient = async () => {
    try { await api.delete(`/clients/${client.id}`); toast.success("Cliente excluído"); navigate("/clients"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir"); }
  };
  const loadEquips = () => api.get("/equipments", { params: { client_id: id } }).then(({ data }) => setEquipList(data)).catch(() => {});
  useEffect(() => { loadEquips(); }, [id]);

  useEffect(() => { api.get(`/clients/${id}`).then(({ data }) => setData(data)).catch(() => { toast.error("Cliente não encontrado"); navigate("/clients"); }); }, [id]);

  const openEdit = () => { setForm({ ...EMPTY_CLIENT, ...Object.fromEntries(Object.keys(EMPTY_CLIENT).map((k) => [k, data.client[k] || ""])) }); setEditing(true); };
  const saveEdit = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome"); return; }
    setSaving(true);
    try {
      await api.put(`/clients/${id}`, form);
      toast.success("Cliente atualizado!");
      setEditing(false);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  if (!data) return <div className="h-64 flex items-center justify-center"><div className="h-10 w-10 rounded-full border-4 border-primary border-t-transparent animate-spin" /></div>;
  const { client, documents, payments, equipments = [] } = data;
  const wa = client.whatsapp?.replace(/\D/g, "");

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/clients")} className="flex items-center gap-1 text-muted-foreground text-sm" data-testid="back-clients-btn"><ArrowLeft className="h-4 w-4" /> Voltar</button>

      <Card className="p-5">
        <div className="flex items-start gap-4">
          <div className="h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-display font-bold text-xl shrink-0">{client.name.slice(0, 1).toUpperCase()}</div>
          <div className="min-w-0 flex-1">
            <h1 className="font-display font-extrabold text-2xl truncate">{client.name}</h1>
            <p className="text-sm text-muted-foreground font-mono">{client.code} {client.document && `• ${client.document}`}</p>
            <div className="flex flex-wrap gap-3 mt-3 text-sm">
              {client.phone && <span className="flex items-center gap-1"><Phone className="h-4 w-4 text-muted-foreground" />{client.phone}</span>}
              {client.email && <span className="flex items-center gap-1"><Mail className="h-4 w-4 text-muted-foreground" />{client.email}</span>}
              {(client.address || client.city) && <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-muted-foreground" />{[[client.address, client.number].filter(Boolean).join(", "), client.neighborhood, client.city && `${client.city}${client.state ? "/" + client.state : ""}`, client.cep].filter(Boolean).join(" • ")}</span>}
            </div>
            {equipments.length > 0 && (
              <div className="mt-3" data-testid="client-equipments">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">Equipamentos</p>
                <div className="flex flex-wrap gap-1.5">
                  {equipments.map((e, i) => <span key={i} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 h-7 text-xs" data-testid={`client-equipment-${i}`}><Wrench className="h-3 w-3 text-primary" />{[e.name, e.brand, e.model].filter(Boolean).join(" ")}</span>)}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="flex-1"><Button className="w-full bg-[#25D366] hover:bg-[#1da851]" data-testid="client-whatsapp-btn"><MessageCircle className="h-4 w-4 mr-1" /> WhatsApp</Button></a>}
          <Button className="flex-1" variant="outline" onClick={() => navigate("/documents/new", { state: { clientId: client.id } })} data-testid="client-new-doc-btn"><Plus className="h-4 w-4 mr-1" /> Novo documento</Button>
          <Button variant="outline" onClick={openEdit} data-testid="client-edit-btn" aria-label="Editar cliente"><Pencil className="h-4 w-4" /></Button>
          {user?.role !== "tecnico" && <Button variant="outline" className="text-destructive hover:bg-destructive/10" onClick={() => setDelOpen(true)} data-testid="client-delete-btn" aria-label="Excluir cliente"><Trash2 className="h-4 w-4" /></Button>}
        </div>
      </Card>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="max-w-sm" data-testid="client-delete-dialog">
          <DialogHeader><DialogTitle>Excluir cliente?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Isso remove <b>{client.name}</b> e todos os documentos e equipamentos vinculados. Essa ação não pode ser desfeita.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={removeClient} data-testid="client-delete-confirm">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar cliente</DialogTitle></DialogHeader>
          <ClientForm form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="save-client-edit-btn">{saving ? "Salvando..." : "Salvar alterações"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="docs">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="docs" data-testid="tab-docs">Documentos ({documents.length})</TabsTrigger>
          <TabsTrigger value="equips" data-testid="tab-equips">Equipamentos ({equipList.length})</TabsTrigger>
          <TabsTrigger value="pays" data-testid="tab-pays">Pagamentos ({payments.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="equips" className="space-y-2 mt-3">
          <Button className="w-full h-12" onClick={() => { setEqInitial(null); setEqOpen(true); }} data-testid="client-new-equipment-btn"><Plus className="h-4 w-4 mr-1" /> Cadastrar equipamento</Button>
          {equipList.length === 0 ? <Card className="p-6 text-center text-muted-foreground text-sm"><Wrench className="h-8 w-8 mx-auto mb-2 opacity-40" />Nenhum equipamento cadastrado.</Card> : equipList.map((e) => (
            <Link to={`/equipments/${e.id}`} key={e.id} data-testid={`client-equip-${e.id}`}>
              <Card className="p-3 flex items-center justify-between gap-2 hover:border-primary transition-colors">
                <div className="min-w-0"><p className="font-semibold text-sm truncate">{e.label}</p><p className="text-xs text-muted-foreground truncate">{[e.serial && `S/N ${e.serial}`, e.location, e.next_maintenance && `Próx. manutenção ${e.next_maintenance}`].filter(Boolean).join(" • ")}</p></div>
                <Badge className={`${MAINT_STYLE[e.maintenance_status]} shrink-0 text-[10px]`}>{MAINT_LABEL[e.maintenance_status]}</Badge>
              </Card>
            </Link>
          ))}
          <EquipmentDialog open={eqOpen} onOpenChange={setEqOpen} clientId={id} initial={eqInitial} onSaved={loadEquips} />
        </TabsContent>
        <TabsContent value="docs" className="space-y-2 mt-3">
          {documents.length === 0 ? <Card className="p-8 text-center text-muted-foreground"><FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />Nenhum documento.</Card> :
            documents.map((d) => (
              <Link to={`/documents/${d.id}`} key={d.id} data-testid={`client-doc-${d.id}`}>
                <Card className="p-4 flex items-center justify-between hover:border-primary transition-colors">
                  <div className="min-w-0"><p className="font-semibold">{DOC_TYPES[d.type]?.label} {d.number}</p><p className="text-xs text-muted-foreground">{d.date}{d.created_at ? ` • ${new Date(d.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : ""}{d.equipment?.name ? ` • ${[d.equipment.name, d.equipment.brand, d.equipment.model].filter(Boolean).join(" ")}` : ""}</p></div>
                  <div className="text-right shrink-0 ml-2">{Number(d.total) > 0 && <p className="font-display font-bold">{formatBRL(d.total)}</p>}<Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[d.status] || ""}`}>{d.status}</Badge></div>
                </Card>
              </Link>
            ))}
        </TabsContent>
        <TabsContent value="pays" className="space-y-2 mt-3">
          {payments.length === 0 ? <Card className="p-8 text-center text-muted-foreground"><Wallet className="h-8 w-8 mx-auto mb-2 opacity-40" />Nenhum pagamento.</Card> :
            payments.map((p) => (
              <Card key={p.id} className="p-4 flex items-center justify-between">
                <div><p className="font-semibold">{p.method}</p><p className="text-xs text-muted-foreground">{p.date}</p></div>
                <div className="text-right"><p className="font-display font-bold text-secondary">{formatBRL(p.amount)}</p><Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[p.status] || ""}`}>{p.status}</Badge></div>
              </Card>
            ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
